/**
 * Locations Service
 *
 * Data access layer for locations table.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { applyAccessControl, type AccessControl } from '../lib/accessControl';
import { mergeLocationTranslation } from '../lib/translations';
import type { Database } from '../lib/database.types';

type LocationInsert = Database['public']['Tables']['locations']['Insert'];
type LocationUpdate = Database['public']['Tables']['locations']['Update'];

/**
 * Fetch all locations
 *
 * @param accessControl - User's access control info for merchant/location filtering
 * @param filters - Optional filters for locations
 * @param language - Language code for translations (default: 'en')
 */
export async function getLocations(
  accessControl: AccessControl | null,
  filters?: {
    active?: boolean;
    deliveries?: boolean;
    searchQuery?: string;
  },
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    let query = supabase
      .from('locations')
      .select('*')
      .order('display_order', { ascending: true })
      .order('location_name', { ascending: true });

    query = applyAccessControl(query, accessControl, { locationIdColumn: 'id' });

    if (filters?.active !== undefined) {
      query = query.eq('active', filters.active);
    }

    if (filters?.deliveries !== undefined) {
      query = query.eq('deliveries', filters.deliveries);
    }

    if (filters?.searchQuery) {
      query = query.or(
        `location_name.ilike.%${filters.searchQuery}%,location_slug.ilike.%${filters.searchQuery}%,pickup_address.ilike.%${filters.searchQuery}%`
      );
    }

    const { data, error } = await query;

    if (error) throw error;

    // Fetch and merge translations only for non-default languages
    // Default language values are stored in the main table.
    // Defensive: skip merge when defaultLanguage is missing so callers that
    // forget to pass it don't accidentally pull in stale translation rows.
    if (defaultLanguage && language !== defaultLanguage) {
      const locationsWithTranslations = await Promise.all(
        (data || []).map(async (location) => {
          const { data: translation } = await supabase
            .from('location_translations')
            .select('*')
            .eq('location_id', location.id)
            .eq('language_code', language)
            .maybeSingle();

          return mergeLocationTranslation(location, translation);
        })
      );

      return { data: locationsWithTranslations, error: null };
    }

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Fetch a single location by ID
 *
 * @param id - Location ID
 * @param accessControl - User's access control info for merchant/location filtering
 * @param language - Language code for translations (default: 'en')
 */
export async function getLocationById(
  id: string,
  accessControl: AccessControl | null,
  language: string = 'en',
  defaultLanguage?: string
) {
  try {
    let query = supabase
      .from('locations')
      .select('*')
      .eq('id', id);

    query = applyAccessControl(query, accessControl, { locationIdColumn: 'id' });

    const { data, error } = await query.maybeSingle();

    if (error) throw error;
    if (!data) return { data: null, error: null };

    // Skip translation merge for the default language —
    // default language values are stored in the main table
    if (defaultLanguage && language === defaultLanguage) {
      return { data, error: null };
    }

    // Fetch translation for non-default language
    const { data: translation } = await supabase
      .from('location_translations')
      .select('*')
      .eq('location_id', data.id)
      .eq('language_code', language)
      .maybeSingle();

    const locationWithTranslation = mergeLocationTranslation(data, translation);

    return { data: locationWithTranslation, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Create a new location
 */
export async function createLocation(location: LocationInsert) {
  try {
    const { data, error } = await supabase
      .from('locations')
      .insert(location)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Update an existing location
 */
export async function updateLocation(id: string, updates: LocationUpdate) {
  try {
    const { data, error } = await supabase
      .from('locations')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Soft delete a location by setting active to false
 */
export async function deactivateLocation(id: string) {
  try {
    const { data, error } = await supabase
      .from('locations')
      .update({
        active: false,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Permanently delete a location
 */
export async function deleteLocation(id: string) {
  try {
    const { error } = await supabase
      .from('locations')
      .delete()
      .eq('id', id);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Get locations count by status
 *
 * @param accessControl - User's access control info for merchant filtering
 */
export async function getLocationsCount(accessControl: AccessControl | null) {
  try {
    let query = supabase
      .from('locations')
      .select('id, active');

    query = applyAccessControl(query, accessControl, { locationIdColumn: 'id' });

    const { data, error } = await query;

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      active: data?.filter((l) => l.active === true).length || 0,
      inactive: data?.filter((l) => l.active === false).length || 0,
    };

    return { data: stats, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Upsert location translation
 * Creates or updates a translation for a specific language
 */
export async function upsertLocationTranslation(
  locationId: string,
  languageCode: string,
  translation: {
    location_name?: string | null;
    pickup_address?: string | null;
    additional_text_checkout?: string | null;
    custom_fields?: any | null;
    confirmation_pickup_text?: string | null;
    confirmation_info_blocks?: any | null;
  }
) {
  try {
    const { data, error } = await supabase
      .from('location_translations')
      .upsert(
        {
          location_id: locationId,
          language_code: languageCode,
          ...translation,
        },
        {
          onConflict: 'location_id,language_code',
        }
      )
      .select()
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Delete a location translation row for a given language.
 *
 * Used to keep the invariant that default-language content lives only in the
 * main `locations` table — any stale row in `location_translations` for the
 * default language should be cleaned up on save so it can't override the
 * freshly-updated main-table value on later reads.
 */
export async function deleteLocationTranslation(locationId: string, languageCode: string) {
  try {
    const { error } = await supabase
      .from('location_translations')
      .delete()
      .eq('location_id', locationId)
      .eq('language_code', languageCode);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Get all translations for a location
 */
export async function getLocationTranslations(locationId: string) {
  try {
    const { data, error } = await supabase
      .from('location_translations')
      .select('*')
      .eq('location_id', locationId);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
